import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, Zap, MessageCircle, ArrowRight, ChevronDown,
  TrendingUp, BarChart3, Shield, Lock, Eye, Clock,
  DollarSign, Compass, Check, X, Calendar, Video,
  User, Brain, Target, Users
} from 'lucide-react';

const HERO_IMG = "https://customer-assets.emergentagent.com/job_702abca6-71b2-428c-8c97-a268c5425284/artifacts/7jujlre6_ChatGPT%20Image%20Apr%2025%2C%202026%2C%2001_13_56%20AM.png";
const WA_LINK = "https://wa.me/601171383699";

const CTAButton = ({ text = "ENQUIRE ON WHATSAPP", className = "" }) => (
  <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
    className={`inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#FCD34D] text-[#061A10] font-bold uppercase tracking-widest rounded-lg transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)] hover:scale-[1.02] active:scale-[0.98] px-10 py-5 text-base ${className}`}>
    <MessageCircle className="w-5 h-5" />
    <span>{text}</span>
    <ArrowRight className="w-5 h-5" />
  </a>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid="faq-item">
      <button className="w-full flex items-center justify-between py-5 text-left group" onClick={() => setOpen(!open)}>
        <span className="text-[#F8FAFC] font-medium pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-[#94A3B8] leading-relaxed">{a}</p>
      </div>
    </div>
  );
};

const AnimatedSection = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} id={id} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </section>
  );
};

const problems = [
  { icon: TrendingUp, title: "Rising Costs", desc: "Inflation eating into household and business budgets alike. The old way of managing wealth no longer works." },
  { icon: BarChart3, title: "Market Chaos", desc: "Global conflicts pulling markets in every direction. Financial systems under pressure that wasn't there 18 months ago." },
  { icon: Shield, title: "Panic Decisions", desc: "People around you making fear-based decisions with their money. A growing sense that the old playbook is broken." },
];

const baziKnows = [
  { icon: Clock, text: "When your strongest earning periods are" },
  { icon: DollarSign, text: "Where your natural wealth advantage sits" },
  { icon: Shield, text: "Which risks will cost you and which will pay off" },
  { icon: Compass, text: "When to push forward and when to protect what you have" },
];

const steps = [
  { num: "01", title: "Diagnose Your Wealth Pattern", problem: "Most people have no idea when their best earning years are, so they push hard at the wrong time and coast when they should be sprinting.", solution: "See exactly where you are in your personal wealth cycle, so you know whether to push, pivot, or protect right now." },
  { num: "02", title: "Identify What Is Blocking You", problem: "You have been working hard for years but the money never seems to match the effort. Something is off and you cannot figure out what.", solution: "Pinpoint the exact elements in your chart that are creating friction so you can remove the block instead of fighting against it." },
  { num: "03", title: "Activate Your Wealth Cycle", problem: "You know something is about to shift, but you have no way of knowing whether it is going to be good or dangerous.", solution: "Learn when your next major wealth window opens and exactly how to position yourself to capture it." },
  { num: "04", title: "Build Your Wealth Protection System", problem: "Even when things are going well, there is always this nagging fear that one wrong move could wipe out years of progress.", solution: "Build a personalised early warning system so you never get blindsided by a downturn or a bad decision again." },
  { num: "05", title: "Execute Your 12-Month Strategy", problem: "You have goals but no framework for deciding when to act on them. Most people just pick a date and hope.", solution: "Walk away with a month-by-month action plan aligned to your personal BaZi timing for maximum impact." },
];

const forYou = [
  "You are a business owner who can feel the economic pressure building",
  "You are a professional or executive who wants a real plan, not just hope",
  "You manage your finances and want to protect your family's future",
  "You want to understand your personal wealth pattern",
  "You would rather prepare now than react later",
];

const notForYou = [
  "You are looking for a get-rich-quick scheme",
  "You want stock tips or crypto picks",
  "You believe the economy will just fix itself",
];

const pricingFeatures = [
  { icon: Calendar, text: "12 months of live coaching" },
  { icon: Video, text: "Live sessions via Zoom" },
  { icon: User, text: "Personal BaZi chart analysis" },
  { icon: Brain, text: "Wealth cycle diagnosis & strategy" },
  { icon: Shield, text: "Wealth protection system" },
  { icon: Target, text: "Goal-specific BaZi activation plan" },
  { icon: MessageCircle, text: "Direct access to Master Steven" },
];

const faqs = [
  { q: "Do I need to know BaZi to join?", a: "No. Master Steven will guide you through reading your chart from scratch. This program is designed for all levels, whether you are a complete beginner or have some BaZi knowledge." },
  { q: "Will I get my personal chart reading?", a: "Yes. You will receive a full personal BaZi chart analysis and learn how to read your own wealth pattern. You walk away with both the skill and the personalised strategy." },
  { q: "How are the coaching sessions conducted?", a: "All sessions are conducted live via Zoom over 12 months. You will have regular scheduled sessions with Master Steven, plus access to replays." },
  { q: "What if I miss a session?", a: "Full replays of all sessions will be available, so you can revisit the material at your own pace. You will never miss any content." },
  { q: "Is this about predicting the stock market?", a: "No. This is about understanding your personal wealth pattern — when to act, when to wait, and how to protect your resources. It is strategic self-awareness, not market speculation." },
  { q: "How is this different from a one-time reading?", a: "A one-time reading gives you a snapshot. 12 months of coaching gives you the ability to read, strategise, and adapt your chart in real-time as your life and the economy evolve." },
];

const BaziMasteryPage = () => {
  return (
    <div className="min-h-screen bg-[#061A10] text-[#F8FAFC] overflow-x-hidden">
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex flex-col" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#061A10] via-[#0A0A0C] to-[#061A10]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#4ADE80]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/5 rounded-full blur-[100px]" />

        {/* Hero Image - Full Width */}
        <div className="relative z-10 w-full">
          <div className="max-w-5xl mx-auto px-6 pt-24">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#4ADE80]/10 backdrop-blur-sm rounded-full border border-[#4ADE80]/20">
              <Sparkles className="w-4 h-4 text-[#4ADE80]" />
              <span className="text-xs text-[#4ADE80] uppercase tracking-widest font-semibold">Bazi Practical & Mastery Program</span>
            </div>
            <img
              alt="Win, Influence and Leverage with Bazi"
              className="w-full max-w-4xl rounded-2xl shadow-[0_20px_60px_-20px_rgba(74,222,128,0.3)]"
              src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/x85dbzcp_Dark%20Gradient%20Astrology%20Forecast%202024%20TouTube%20Thumbnail%20%20%2817%29.jpg"
              data-testid="hero-product-img"
            />
          </div>
        </div>

        {/* Content Below */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-lg text-[#94A3B8] max-w-lg mb-8 leading-relaxed">
                Strategise, Influence & Win. A 12-month live coaching program with{' '}
                <span className="text-[#D4AF37] font-semibold">Master Steven Yong</span> to decode your personal wealth pattern and activate your destiny cycle.
              </p>
            </div>
            <div className="flex flex-col items-start gap-4">
              <CTAButton />
              <a href="#pricing" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#FCD34D] font-semibold transition-colors py-2">
                <span>See Pricing</span>
                <ChevronDown className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <AnimatedSection id="problem" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
            <span className="text-[#F8FAFC]">You Can Feel It.</span>{' '}<span className="text-[#D4AF37]">Something Has Changed.</span>
          </h2>
          <p className="text-[#94A3B8] text-center max-w-2xl mx-auto mb-16 leading-relaxed">
            Everything costs more than it did two years ago. Your savings are not stretching the way they used to. The news gets worse every week, and nobody seems to have a real answer.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl hover:border-[#4ADE80]/20 transition-all duration-300 group">
                  <div className="p-3 bg-[#4ADE80]/10 rounded-lg w-fit mb-4 group-hover:bg-[#4ADE80]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-[#F8FAFC] font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== TWO PATHS ===== */}
      <AnimatedSection className="py-24 px-6 bg-[#0A0A0C]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">
            Right Now, You Are Probably{' '}<span className="text-[#D4AF37]">Doing One of These</span>
          </h2>
          <p className="text-[#94A3B8] text-center mb-16">And both will cost you.</p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative p-8 bg-white/[0.02] border border-red-500/20 rounded-2xl group hover:border-red-500/40 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full blur-2xl" />
              <div className="p-3 bg-red-500/10 rounded-lg w-fit mb-4"><Lock className="w-6 h-6 text-red-400" /></div>
              <h3 className="text-xl font-semibold text-[#F8FAFC] mb-3">Freezing Up</h3>
              <p className="text-[#94A3B8] leading-relaxed">Pulling back on everything. Hoarding cash. Cancelling plans. Telling yourself you will wait until things are "stable" again. Meanwhile, the people who are positioned correctly are quietly making moves you are too afraid to consider.</p>
            </div>
            <div className="relative p-8 bg-white/[0.02] border border-orange-500/20 rounded-2xl group hover:border-orange-500/40 transition-all">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 rounded-full blur-2xl" />
              <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4"><Eye className="w-6 h-6 text-orange-400" /></div>
              <h3 className="text-xl font-semibold text-[#F8FAFC] mb-3">Pretending Nothing Changed</h3>
              <p className="text-[#94A3B8] leading-relaxed">Managing your money the same way you did in 2019. Assuming things will sort themselves out. Hoping for the best. You will not feel the damage immediately. But by the time you do, the window to prepare will be closed.</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <p className="text-[#F8FAFC] font-semibold mb-2">Neither of these is a strategy.</p>
            <p className="text-[#94A3B8]">
              The people who come out of economic shifts with more than they started are the ones who{' '}
              <span className="text-[#D4AF37] font-semibold">had a plan before the crowd started reacting.</span>
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== BAZI ADVANTAGE ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
            The Advice You Are Getting{' '}<span className="text-[#D4AF37]">Was Not Built for You</span>
          </h2>
          <p className="text-[#94A3B8] text-center max-w-2xl mx-auto mb-16 leading-relaxed">
            Every financial tip you read online was written for a generic person. None of it accounts for <em>your</em> timing, <em>your</em> strengths, <em>your</em> risk windows.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {baziKnows.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-start gap-4 p-5 bg-[#4ADE80]/5 border border-[#4ADE80]/10 rounded-xl">
                  <Icon className="w-5 h-5 text-[#4ADE80] shrink-0 mt-0.5" />
                  <span className="text-[#F8FAFC]">{item.text}</span>
                </div>
              );
            })}
          </div>
          <p className="text-center mt-12 text-[#94A3B8]">
            This is not "save more, spend less" advice.{' '}
            <span className="text-[#D4AF37] font-semibold">This is a personalised wealth strategy based on your chart, your timing, and your strengths.</span>
          </p>
        </div>
      </AnimatedSection>

      {/* ===== 5-STEP SYSTEM ===== */}
      <AnimatedSection id="system" className="py-24 px-6 bg-[#0A0A0C]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#4ADE80] uppercase tracking-widest font-semibold block mb-4">The System</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              <span className="text-[#F8FAFC]">Growth</span>{' '}<span className="text-[#D4AF37]">Wealth</span>{' '}<span className="text-[#4ADE80]">with BaZi</span>
              <br />The 5-Step{' '}<span className="text-[#D4AF37]">Wealth Cycle</span>{' '}System</h2>
            <p className="text-[#94A3B8] mt-4 max-w-lg mx-auto">What you will walk away with after 12 months of coaching</p>
          </div>
          <div className="space-y-6">
            {steps.map((step, i) => (
              <div key={i} className="group grid md:grid-cols-[80px_1fr] gap-6 p-6 md:p-8 bg-white/[0.02] border border-white/[0.06] rounded-2xl hover:border-[#D4AF37]/30 transition-all duration-300">
                <div className="text-5xl font-bold text-[#D4AF37]/30 group-hover:text-[#D4AF37]/60 transition-colors">{step.num}</div>
                <div>
                  <h3 className="text-xl font-semibold text-[#F8FAFC] mb-3">{step.title}</h3>
                  <p className="text-[#94A3B8] text-sm mb-3 leading-relaxed">{step.problem}</p>
                  <div className="flex items-start gap-2 p-4 bg-[#4ADE80]/5 border border-[#4ADE80]/10 rounded-lg">
                    <Check className="w-5 h-5 text-[#4ADE80] shrink-0 mt-0.5" />
                    <span className="text-[#F8FAFC] text-sm">{step.solution}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FOR YOU / NOT FOR YOU ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-16 text-center">
            Is This{' '}<span className="text-[#D4AF37]">Right for You?</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-[#4ADE80] text-sm uppercase tracking-widest font-semibold mb-4">This is for you if:</p>
              <div className="space-y-4">
                {forYou.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#4ADE80] shrink-0 mt-0.5" />
                    <span className="text-[#F8FAFC]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[#94A3B8] text-sm uppercase tracking-widest mb-4">This is NOT for you if:</p>
              <div className="space-y-4">
                {notForYou.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <span className="text-[#94A3B8]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== WHAT YOU WILL LEARN ===== */}
      <AnimatedSection className="py-24 px-6 bg-[#0A0A0C]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#4ADE80] uppercase tracking-widest font-semibold block mb-4">Curriculum</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              What You Will Learn In This{' '}<span className="text-[#D4AF37]">12-Month Mastery Program</span>
            </h2>
          </div>
          <div className="space-y-4">
            {[
              "The key fundamentals of BaZi and to understand its 'blueprint' of one's life and destiny",
              "To understand the concept of life purpose & destiny and the keys to unlock one's full potential",
              "To understand how our mindset and characteristics that are inborn & also shaped mainly during early life affect our decisions & actions which in turn, determine our life path",
              "To take destiny into your own hands by making wiser decisions & taking optimum actions towards your ultimate destiny through a shift in beliefs & perspectives",
              "To perform detailed analysis on work/career, relationship & health aspects of one's life",
              "To read charts as a practitioner by the end of the 12 months!",
              "The tools, knowledge & guidance towards becoming a Bazi Consultant",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:border-[#4ADE80]/20 transition-all duration-300">
                <div className="shrink-0 w-8 h-8 rounded-lg bg-[#4ADE80]/10 flex items-center justify-center mt-0.5">
                  <span className="text-[#4ADE80] text-sm font-bold">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <span className="text-[#F8FAFC] leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== BAZI INTELLIGENCE PLATFORM ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#4ADE80] uppercase tracking-widest font-semibold block mb-4">Included Platform Access</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Powerful BaZi{' '}<span className="text-[#D4AF37]">Intelligence Platform</span>
            </h2>
            <p className="text-[#94A3B8] mt-4 max-w-2xl mx-auto">
              As part of your mastery program, you get full access to our modern BaZi analysis platform — transforming ancient wisdom into actionable intelligence.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: BarChart3, title: "BaZi Chart Engine", desc: "Generate your complete Four Pillars chart instantly. Heavenly Stems, Earthly Branches, and Hidden Stems revealed." },
              { icon: Clock, title: "Luck Pillars Timeline", desc: "See how 10-year cycles influence your life direction and opportunities. Know when to push forward and when to consolidate." },
              { icon: Calendar, title: "Monthly Energy Forecast", desc: "Understand when to act, plan, invest or rest based on monthly energy cycles aligned with your personal chart." },
              { icon: Users, title: "10 Profiles Analysis", desc: "Discover your strengths: Director, Performer, Leader, Artist, Friend, Philosopher, Warrior, Pioneer, Diplomat, Guardian." },
              { icon: Brain, title: "Five Structures Map", desc: "Reveal whether you are a Connector, Creator, Manager, Supporter, or Thinker through the Five Structures framework." },
              { icon: Target, title: "Date Selection Intelligence", desc: "Identify the best days for business decisions, important meetings, investments, and significant life events." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:border-[#4ADE80]/20 transition-all duration-300 group">
                  <div className="p-3 bg-[#4ADE80]/10 rounded-lg w-fit mb-4 group-hover:bg-[#4ADE80]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-[#F8FAFC] font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Five Elements */}
          <div className="text-center">
            <h3 className="text-xl font-bold mb-3 text-[#F8FAFC]">Ancient System, <span className="text-[#D4AF37]">Modern Platform</span></h3>
            <p className="text-[#94A3B8] max-w-2xl mx-auto mb-10 text-sm leading-relaxed">
              BaZi, also known as the Four Pillars of Destiny, maps human destiny using the Five Elements, Heavenly Stems and Earthly Branches derived from your birth date and time. Our platform transforms this into a modern analytical dashboard.
            </p>
            <div className="flex items-center justify-center gap-6 sm:gap-10">
              {[
                { char: "木", label: "Wood", color: "text-green-400" },
                { char: "火", label: "Fire", color: "text-red-400" },
                { char: "土", label: "Earth", color: "text-yellow-500" },
                { char: "金", label: "Metal", color: "text-gray-300" },
                { char: "水", label: "Water", color: "text-blue-400" },
              ].map((el, i) => (
                <div key={i} className="text-center">
                  <div className={`text-3xl sm:text-4xl font-serif ${el.color} mb-1`}>{el.char}</div>
                  <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{el.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== PRICING ===== */}
      <AnimatedSection id="pricing" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold block mb-4">Investment</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              12 Months That Could Change{' '}<span className="text-[#D4AF37]">How You Handle Wealth for Life</span>
            </h2>
          </div>
          <div className="max-w-lg mx-auto relative" data-testid="pricing-card">
            <div className="absolute inset-0 bg-[#D4AF37]/10 blur-3xl rounded-3xl" />
            <div className="relative bg-[#0A0A0C] border-2 border-[#D4AF37]/40 rounded-2xl p-8 sm:p-10 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.3)]">
              <div className="text-center">
                <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-semibold mb-6">Bazi Mastery Coaching</p>
                <div className="mb-6">
                  <span className="text-5xl sm:text-6xl font-bold text-[#F8FAFC]">USD 3,000</span>
                </div>
                <div className="h-px w-full bg-white/10 mb-6" />
                <div className="space-y-4 text-left mb-8">
                  {pricingFeatures.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-[#4ADE80]" />
                        <span className="text-[#F8FAFC]">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
                <CTAButton className="w-full justify-center" />
                <p className="text-xs text-[#94A3B8] mt-4">Limited spots available. Speak with us to reserve yours.</p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FAQ ===== */}
      <AnimatedSection id="faq" className="py-24 px-6 bg-[#0A0A0C]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">
            Common{' '}<span className="text-[#D4AF37]">Questions</span>
          </h2>
          <div>
            {faqs.map((item, i) => <FaqItem key={i} q={item.q} a={item.a} />)}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 px-6 relative" data-testid="final-cta">
        <div className="absolute inset-0 bg-gradient-to-t from-[#061A10] via-[#0A0A0C] to-[#061A10]" />
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            You Already Know{' '}<span className="text-[#D4AF37]">Something Needs to Change</span>
          </h2>
          <p className="text-[#94A3B8] max-w-lg mx-auto mb-10 leading-relaxed">
            You can keep doing what you have been doing and hope the economy sorts itself out. Or you can invest in understanding your personal wealth strategy so you are not guessing when the pressure hits.
          </p>
          <CTAButton />
          <p className="text-sm text-[#94A3B8] mt-6">USD 3,000 · 12 Months Live Coaching · Limited Spots</p>
        </div>
      </section>
    </div>
  );
};

export default BaziMasteryPage;
